import {
  defaultLoadableState,
  LoadableState,
  useLoadableState
} from '@carvana/vdp-react-hooks';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import type { Pagination, UserDeliveryInfo, Vehicle } from '~/contracts';
import PricingResponse from '~/services/contracts/PricingResponse';
import isServerSideRendering from '~/utilities/IsServerSideRendering';

function getPageNumberFromUrl(): number {
  if (isServerSideRendering) return 1;

  const pageNumber = parseInt(
    new URL(window.location.href).searchParams.get('page') ?? '1',
    10
  );

  return pageNumber <= 1 ? 1 : pageNumber;
}

export type Inventory = {
  inventory: {
    pagination: Partial<Pagination>;
    vehicles: Vehicle[];
  };
  searchRequestId: string;
  userDeliveryInfo?: UserDeliveryInfo;
};

export type SimilarVehicles = { vehicles: Vehicle[]; searchRequestId: string };

type InventoryContextType = {
  fetchInventoryTrigger: number;
  inventory: LoadableState<Inventory>;
  resetInventoryPricing: () => void;
  setSimilarVehicles: React.Dispatch<React.SetStateAction<SimilarVehicles>>;
  similarVehicles: SimilarVehicles;
  triggerFetchInventory: () => void;
  updateInventoryPricing: (_data: PricingResponse) => void;
};

export const defaultInventory: Inventory = {
  inventory: {
    pagination: {
      currentPage: getPageNumberFromUrl()
    },
    vehicles: []
  },
  searchRequestId: '',
  userDeliveryInfo: undefined
};

const defaultContext: InventoryContextType = {
  fetchInventoryTrigger: 0,
  inventory: defaultLoadableState<Inventory>(defaultInventory),
  resetInventoryPricing() {},
  setSimilarVehicles() {},
  similarVehicles: {
    searchRequestId: '',
    vehicles: []
  },
  triggerFetchInventory() {},
  updateInventoryPricing() {}
};

const InventoryContext = createContext<InventoryContextType>(defaultContext);

export type OwnProps = {
  inventoryData?: Inventory;
  similarVehiclesFromServer?: SimilarVehicles;
};

export function InventoryContextProvider({
  children,
  inventoryData,
  similarVehiclesFromServer
}: React.PropsWithChildren<OwnProps>) {
  const loadableInventoryState = useLoadableState<Inventory>(
    inventoryData ?? defaultInventory,
    { initializeAsLoaded: inventoryData != null }
  );
  const [similarVehicles, setSimilarVehicles] = useState<SimilarVehicles>(
    similarVehiclesFromServer ?? defaultContext.similarVehicles
  );

  const [fetchInventoryTrigger, setTrigger] = useState(
    defaultContext.fetchInventoryTrigger
  );

  const triggerFetchInventory = useCallback(() => {
    setTrigger(Date.now());
  }, []);

  const updateInventoryPricing = useCallback(
    (payload: PricingResponse) => {
      loadableInventoryState.setData.call(
        null,
        prevState => ({
          ...prevState,
          inventory: {
            ...prevState.inventory,
            vehicles: prevState.inventory.vehicles.map(vehicle => {
              const { termInMonths, ...pricingData } =
                payload.vehiclePaymentTermsMapping[vehicle.vehicleId] ?? {};

              return {
                ...vehicle,
                price: {
                  ...vehicle.price,
                  ...pricingData,
                  term: termInMonths
                }
              };
            })
          }
        }),
        { shouldSetLoaded: false }
      );
    },
    [loadableInventoryState.setData]
  );

  const resetInventoryPricing = useCallback(() => {
    loadableInventoryState.setData.call(
      null,
      prevState => ({
        ...prevState,
        inventory: {
          ...prevState.inventory,
          vehicles: prevState.inventory.vehicles.map(vehicle => {
            return {
              ...vehicle,
              price: {
                ...vehicle.price,
                downPayment: 0,
                monthlyPayment: 0
              }
            };
          })
        }
      }),
      { shouldSetLoaded: false }
    );
  }, [loadableInventoryState.setData]);

  const value = useMemo<InventoryContextType>(
    () => ({
      fetchInventoryTrigger,
      inventory: loadableInventoryState,
      setSimilarVehicles,
      resetInventoryPricing,
      similarVehicles,
      triggerFetchInventory,
      updateInventoryPricing
    }),
    [
      fetchInventoryTrigger,
      loadableInventoryState,
      resetInventoryPricing,
      similarVehicles,
      triggerFetchInventory,
      updateInventoryPricing
    ]
  );

  return (
    <InventoryContext.Provider value={value}>
      {children}
    </InventoryContext.Provider>
  );
}

InventoryContextProvider.displayName = 'InventoryContextProvider';

export function useInventoryIsLoaded(): boolean {
  const { inventory } = useContext(InventoryContext);

  return inventory.isLoaded;
}

export function useInventoryVehicles(): Vehicle[] {
  const { inventory } = useContext(InventoryContext);

  return inventory.data?.inventory?.vehicles ?? [];
}

export function usePagination(): Partial<Pagination> {
  const { inventory } = useContext(InventoryContext);

  return inventory.data?.inventory?.pagination ?? {};
}

export function useSearchRequestId(): string {
  const { inventory } = useContext(InventoryContext);

  return inventory.data?.searchRequestId ?? '';
}

export function useSimilarVehicles() {
  const { similarVehicles } = useContext(InventoryContext);

  return similarVehicles;
}

export function useUserDeliveryInfo(): UserDeliveryInfo | undefined {
  const { inventory } = useContext(InventoryContext);

  return inventory.data?.userDeliveryInfo;
}

export default InventoryContext;
